import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, UrlSegment } from '@angular/router';
import moment from 'moment';
import { NgForage } from 'ngforage';
import { IAlbum, Lightbox } from 'ngx-lightbox';
import { combineLatest, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import ErrorHelper from 'src/app/helpers/error-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import { RouteHelper } from 'src/app/helpers/route-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import UserHelper from 'src/app/helpers/user-helper';
import {
  getValueFromResolvedOrPromise,
  ResolvedOrPromise,
} from 'src/app/helpers/utility-functions';
import Branch from 'src/app/model/branch/branch';
import {
  BoardTypeChoiceArray,
  BoardTypeChoices,
} from 'src/app/model/product/board/board-part';
import BoardType from 'src/app/model/product/board/board-type';
import PlacementType from 'src/app/model/product/board/placement-type';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { IPBoardPlacementTypeChoicesArray } from 'src/app/model/product/ip-board/ip-board-placement-type';
import Product from 'src/app/model/product/product';
import Address from 'src/app/model/project/address';
import { Adjustment } from 'src/app/model/project/adjustment';
import { ProjectPlacement } from 'src/app/model/project/projectPlacement';
import { SoldRented } from 'src/app/model/project/sold-rented';
import {
  SUB_PROJECT_TYPES,
  SubProject,
} from 'src/app/model/project/sub-project';
import RegisteredAction from 'src/app/model/registered-action';
import { Tour } from 'src/app/model/tour/tour';
import { User } from 'src/app/model/user';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { formatFirstProduct } from 'src/app/pages/projects/helpers/first-product/first-product-helper';
import {
  GroupProjectContactPipe,
  ProjectContactGroup,
} from 'src/app/pipes/group-project-contact';
import { PrintAddressPipe } from 'src/app/pipes/print-address.pipe';
import { AuthService } from 'src/app/services/auth.service';
import { OfflineBackedBranchChoicesService } from 'src/app/services/branch/offline-backed-branch-choices.service';
import { ImmoIPChangesService } from 'src/app/services/immo-ip-changes/immo-ip-changes-service';
import { ImmoIPProductChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes.service';
import { ImmoIPNotExecutedHistoryItemObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-not-executed-history-item-object';
import { ImmoIPSubProjectChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes.service';
import { ToDoItem } from 'src/app/services/immo-ip-placer-offline-changes/model/to-do-item';
import { ImmoIPPlacerSubmitInProgressService } from 'src/app/services/immo-ip-placer-submit/immo-ip-placer-submit-in-progress.service';
import { ImmoIPPlacerSubmitQueueService } from 'src/app/services/immo-ip-placer-submit/immo-ip-placer-submit-queue.service';
import { ImmoIPPlacerSubmitService } from 'src/app/services/immo-ip-placer-submit/immo-ip-placer-submit.service';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { PlanningDateService } from 'src/app/services/planning-date-service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { INT_MAX_SIGNED_32_BIT_VALUE } from 'src/globals/globals';
import { DisplayDateTimePipe } from '../../../../pipes/display-date-time.pipe';
import { ImmoIPPlacerTourViewHelper } from '../tour-view/helper/immo-ip-placer-tour-view-helper';
import {
  OfflineStorageTourService,
  SubProjectWithChangeType,
} from '../tour-view/offline-storage/offline-storage-tour.service';
import { ProjectToOtherTour } from '../tour-view/offline-storage/project-to-other-tour-storage/project-to-other-tour';
import { ProjectToOtherTourStorageService } from '../tour-view/offline-storage/project-to-other-tour-storage/project-to-other-tour-storage.service';
import { CompletedAssignmentModalComponent } from '../../../../shared/completed-assignment-modal/completed-assignment-modal.component';
import { ImmoIPPlacerTourItemDetailChangeStorageHandler } from './helper/immo-ip-placer-tour-item-detail-change-storage-handler';
import {
  AppointmentInfo,
  ImmoIPPlacerTourItemDetailHelper,
  SubProjectAssets,
  SubProjectExecutionState,
  SubProjectToDoItems,
} from './helper/immo-ip-placer-tour-item-detail-helper';
import {
  TourItemDetailAsset,
  TourItemDetailProductWrapper,
} from './helper/model/tour-item-detail-product-wrapper';
import { TourItemDetailSoldRentedStickerInfo } from './helper/model/tour-item-detail-sold-rented-sticker-info';
import { ImmoIPPlacerTourItemDetailNotExecutedModalHelper } from './not-executed-modal/immo-ip-placer-tour-item-detail-not-executed-modal-helper';
import { ImmoIPPlacerTourItemDetailNotExecutedModalComponent } from './not-executed-modal/immo-ip-placer-tour-item-detail-not-executed-modal.component';

@Component({
  selector: 'immo-ip-placer-tour-item-detail',
  templateUrl: './immo-ip-placer-tour-item-detail.component.html',
  styleUrls: ['./immo-ip-placer-tour-item-detail.component.scss'],
  providers: [
    GroupProjectContactPipe,
    FormatPhoneNumber,
    PrintAddressPipe,
    DisplayDateTimePipe,
  ],
})
export class ImmoIPPlacerTourItemDetailComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  init = false;
  reloading = false;

  SUB_PROJECT_TYPES = SUB_PROJECT_TYPES;
  placementTypeChoicesArray = IPBoardPlacementTypeChoicesArray.filter(
    (x) => x.ordinal != INT_MAX_SIGNED_32_BIT_VALUE && x.ordinal != 5
  );
  boardTypeChoicesArray = BoardTypeChoiceArray;

  user: User;
  placerName: string;
  planningDate: string;

  subProject: SubProject;
  firstIPBoardWrapper: TourItemDetailProductWrapper;

  projectContactGroups: ProjectContactGroup[];
  timeBudgetDisplay: string;
  appointmentInfo: AppointmentInfo;

  adjustment: Adjustment;

  baseProjectPlacement: ProjectPlacement;
  baseProjectTour: Tour;
  baseProjectDoneRegistration: RegisteredAction;

  soldRentedStickerInfo: TourItemDetailSoldRentedStickerInfo;

  subProjectToDoItems: SubProjectToDoItems;

  executionState: SubProjectExecutionState;
  submitInProgress = false;

  immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler;

  notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject;
  shouldReloadNotExecutedHistoryItemAssets = new Subject<void>();

  lowerCaseType: string;
  subProjectAssets: SubProjectAssets;

  // Assets uploaded by client/admin
  adjustmentExternalAssets: any[] = [];
  // Merged assets to display as thumbnails
  adjustmentDisplayAssets: any[] = [];

  private date: Date;

  showPlacementConfigModal = false;
  selectedBoardType: BoardType;
  selectedPlacementType: PlacementType;
  saved = false;

  stockedAtBranch: boolean | null = null;
  stockedAtBranchSubtitle: string;
  branchChoices: Branch[] = [];

  // Matrix of available installer PDFs by board type and placement type
  // Board types: 0=SINGLE_SIDED, 1=DOUBLE_SIDED, 2=V
  // Placement types: 0=Gevel, 1=Balkon, 2=Opgespannen, 3=Op palen, 4=Stenen
  // Note: Ordinal 5 (WINDOW) is excluded from placementTypeChoicesArray
  // Each entry can be a string (single PDF) or object (multiple PDF options)
  installerPdfMatrix = {
    // Enkel bord (SINGLE_SIDED)
    0: {
      0: '', // Gevel - No PDF yet
      1: '', // Balkon - No PDF yet
      2: '', // Opgespannen - No PDF yet
      3: '', // Op palen - No PDF yet
      4: '', // Stenen - No PDF yet
      5: '', // WINDOW - No PDF yet
    },
    // Dubbelzijdig bord (DOUBLE_SIDED)
    1: {
      0: '', // Gevel - No PDF yet
      1: '', // Balkon - No PDF yet
      2: '', // Opgespannen - No PDF yet
      3: '', // Op palen - No PDF yet
      4: '', // Stenen - No PDF yet
      5: '', // WINDOW - No PDF yet
    },
    // V-bord (V)
    2: {
      0: {
        regular: 'vbord-gevel.pdf',
        // vertical: 'vbord-gevel-verticaal.pdf',
      },
      1: 'vbord-gevel-balkon.pdf', // Balkon - Single option
      2: 'vbord-opgespannen.pdf', // Opgespannen
      3: 'vbord-op-palen.pdf', // Op palen - Single option
      4: '', // Stenen - No PDF yet
      5: '', // WINDOW - Excluded from installer matrix
    },
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private offlineStorageTourService: OfflineStorageTourService,
    private projectInOtherTourStorageService: ProjectToOtherTourStorageService,
    private ngForage: NgForage,
    private immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    private immoIPProductChangesService: ImmoIPProductChangesService,
    private authService: AuthService,
    private immoIPPlacerSubmitService: ImmoIPPlacerSubmitService,
    private immoIPPlacerSubmitQueueService: ImmoIPPlacerSubmitQueueService,
    private immoIPPlacerSubmitInProgressService: ImmoIPPlacerSubmitInProgressService,
    private modalService: ModalService,
    private snackService: SnackService,
    private groupProjectContactsPipe: GroupProjectContactPipe,
    private formatPhoneNumber: FormatPhoneNumber,
    private printAddressPipe: PrintAddressPipe,
    private dateTimePipe: DisplayDateTimePipe,
    private _lightbox: Lightbox,
    private planningDateService: PlanningDateService,
    private immoIPChangesService: ImmoIPChangesService,
    private branchChoicesService: OfflineBackedBranchChoicesService
  ) {
    super();
  }

  ngOnInit(): void {
    const routeObservable = combineLatest([
      this.route.params,
      this.route.url,
    ]).pipe(take(1));
    this.addSubscription(
      routeObservable,
      ([params, urlSegments]: [Params, UrlSegment[]]) => {
        this.handleLoadSubProject(params, urlSegments);
      }
    );
    this.addSubscription(
      this.offlineStorageTourService.getSubProjectHasChangedObservable(),
      async (subProjectWithChangeType: SubProjectWithChangeType) => {
        if (
          subProjectWithChangeType.subProject != null &&
          this.subProject.id === subProjectWithChangeType.subProject.id
        ) {
          this.reloading = true;
          try {
            await ImmoIPPlacerTourItemDetailHelper.handleSubProjectChanged(
              this.subProject,
              subProjectWithChangeType.subProject,
              this.firstIPBoardWrapper,
              subProjectWithChangeType.changeType,
              this.immoIPPlacerTourItemDetailChangeStorageHandler
            );
            this.executionState =
              ImmoIPPlacerTourItemDetailHelper.getSubProjectExecutionState(
                this.subProject,
                this.firstIPBoardWrapper,
                this.subProjectToDoItems
              );
          } finally {
            this.reloading = false;
          }
        }
      }
    );
    this.handleSubmitInProgressChange();

    this.addSubscription(
      this.planningDateService.getSelectedPlanningDateSubscribable(),
      (date: Date) => {
        this.date = date;
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.immoIPPlacerTourItemDetailChangeStorageHandler != null) {
      this.immoIPPlacerTourItemDetailChangeStorageHandler.destroy();
    }
    if (this.shouldReloadNotExecutedHistoryItemAssets != null) {
      this.shouldReloadNotExecutedHistoryItemAssets.complete();
      this.shouldReloadNotExecutedHistoryItemAssets = null;
    }
  }

  private async initializePlacer(ownTour: boolean): Promise<void> {
    this.user = await this.authService
      .waitUntilAuthenticatedUserLoadedSubscribable()
      .toPromise();
    if (ownTour) {
      this.placerName = UserHelper.getDisplayName(this.user);
    }
  }

  private handleLoadSubProject(
    params: Params,
    urlSegments: UrlSegment[]
  ): void {
    if (params == null || !CollectionHelper.isPopulated(urlSegments)) {
      throw new Error('Ongeldige route');
    }
    if (RouteHelper.urlSegmentsContain(urlSegments, 'opdracht')) {
      this.handleLoadOwnSubProject(params);
    } else if (
      RouteHelper.urlSegmentsContain(urlSegments, 'opdracht-andere-toer')
    ) {
      this.handleLoadOtherTourSubProject(params);
    } else {
      throw new Error('Ongeldige route');
    }
  }

  private handleLoadOwnSubProject(params: Params): void {
    this.initializePlacer(true);
    const dateString =
      params['dateString'] == null ? null : params['dateString'];
    if (StringHelper.isEmpty(dateString)) {
      this.snackService.showError('dateString ontbreekt');
      return;
    }
    const date = DateHelper.parseDate(dateString);
    this.planningDate = DateHelper.formatDate(date, 'dd/MM/yyyy');
    const subProjectId =
      params['subProjectId'] == null ? null : Number(params['subProjectId']);
    if (subProjectId == null) {
      this.snackService.showError('subProjectId ontbreekt');
      return;
    }
    this.doLoadSubProject(
      {
        promise: this.offlineStorageTourService.getSubProject(
          dateString,
          subProjectId,
          null
        ),
      },
      dateString
    );
  }

  private handleLoadOtherTourSubProject(params: Params): void {
    const projectId =
      params['projectId'] == null ? null : Number(params['projectId']);
    if (projectId == null) {
      this.snackService.showError('projectId ontbreekt');
      return;
    }
    this.initializePlacer(false);
    this.projectInOtherTourStorageService
      .getObject(projectId)
      .then((projectToOtherTour: ProjectToOtherTour) => {
        if (
          projectToOtherTour == null ||
          projectToOtherTour.subProject == null
        ) {
          this.snackService.showError('Kon job niet laden');
          return;
        }
        if (projectToOtherTour.tourDateString == null) {
          this.snackService.showError('tourDateString ontbreekt');
          return;
        }
        if (projectToOtherTour.user != null) {
          this.placerName = UserHelper.getDisplayName(projectToOtherTour.user);
        }
        if (!StringHelper.isEmpty(projectToOtherTour.tourDateString)) {
          const date = DateHelper.parseDate(projectToOtherTour.tourDateString);
          this.planningDate = DateHelper.formatDate(date, 'dd/MM/yyyy');
        }
        this.doLoadSubProject(
          { value: projectToOtherTour.subProject },
          projectToOtherTour.tourDateString
        );
      });
  }

  private handleSubmitInProgressChange(): void {
    this.addSubscription(
      this.immoIPPlacerSubmitInProgressService.getSubmitInProgressChangedSubscribable(),
      () =>
        this.immoIPPlacerSubmitInProgressService
          .isInProgress(this.subProject.id)
          .then((inProgress: boolean) => (this.submitInProgress = inProgress))
    );
  }

  private async doLoadSubProject(
    loadSubProjectResolvedOrPromise: ResolvedOrPromise<SubProject>,
    dateString: string
  ): Promise<void> {
    try {
      this.subProject = await getValueFromResolvedOrPromise(
        loadSubProjectResolvedOrPromise
      );
      this.lowerCaseType = this.subProject.type.toLocaleLowerCase();
      this.subProject.projectPlacement.placeASAP =
        this.subProject.currentRequestASAP;
      this.subProject.projectPlacement.placementEarliestDate =
        this.subProject.currentRequestDate;
      if (this.subProject.project != null) {
        if (
          this.subProject.project.products != null &&
          this.subProject.project.products.length > 0
        ) {
          if (
            ProductHelper.isIPBoard(this.subProject.project.products[0]) &&
            (this.subProject.project.products[0] as IPBoard).boardPart != null
          ) {
            this.subProject.project.products[0] = formatFirstProduct(
              this.subProject.project.products[0]
            );
          }
          this.subProject.project.firstProduct = this.subProject.project
            .products[0] as any;
          this.selectedBoardType =
            this.subProject.project.firstProduct.boardPart.boardType;
          this.selectedPlacementType =
            this.subProject.project.firstProduct.boardPart.placementType;
        }
      }
      this.subProjectAssets =
        ImmoIPPlacerTourItemDetailHelper.getSubProjectAssets(this.subProject);

      this.adjustmentExternalAssets = (
        this.subProject?.projectPlacement?.projectPlacementAssets || []
      )
        .filter(
          (pa: any) =>
            pa &&
            !pa.archived &&
            pa.asset != null &&
            pa.formSection === 'adjustmentExternal'
        )
        .map((pa: any) => pa.asset);

      const merged: any[] = [];
      const seen = new Set<string>();
      const addAsset = (a: any) => {
        if (!a || !a.uuid || seen.has(a.uuid)) return;
        seen.add(a.uuid);
        merged.push({
          ...a,
          backgroundImageStyle: {
            'background-image': `url('/api/assets/${a.uuid}/330/225')`,
            'background-position': 'center',
            'background-repeat': 'no-repeat',
            'background-size': 'contain',
          },
        });
      };
      (this.subProjectAssets?.designAssets || []).forEach(addAsset);
      (this.adjustmentExternalAssets || []).forEach(addAsset);
      this.adjustmentDisplayAssets = merged;

      if (
        this.subProject.project == null ||
        this.subProject.project.id == null
      ) {
        throw new Error('Subjob heeft geen hoofdjob');
      }
      if (this.immoIPPlacerTourItemDetailChangeStorageHandler != null) {
        const previousChangeStorageHandler =
          this.immoIPPlacerTourItemDetailChangeStorageHandler;
        this.immoIPPlacerTourItemDetailChangeStorageHandler = null;
        previousChangeStorageHandler.destroy();
      }
      this.immoIPPlacerTourItemDetailChangeStorageHandler =
        await ImmoIPPlacerTourItemDetailChangeStorageHandler.initialize(
          this.subProject,
          dateString,
          this.ngForage,
          this.immoIPSubProjectChangesService,
          this.immoIPProductChangesService,
          this.immoIPPlacerSubmitService,
          this.immoIPPlacerSubmitQueueService,
          this.snackService
        );
      this.notExecutedHistoryItemObject =
        this.immoIPPlacerTourItemDetailChangeStorageHandler.getCurrentNotExecutedHistoryItemObject();
      if (
        this.notExecutedHistoryItemObject != null &&
        this.notExecutedHistoryItemObject.id != null
      ) {
        this.stockedAtBranch =
          this.notExecutedHistoryItemObject.stockAtBranchId != null;
        this.stockedAtBranchSubtitle =
          ImmoIPPlacerTourItemDetailNotExecutedModalHelper.getStockedAtBranchSubtitle(
            this.subProject.type
          );
      }
      this.addSubscription(
        this.branchChoicesService.getBranchChoicesSubscribable(),
        (branches: Branch[]) => (this.branchChoices = branches)
      );
      ImmoIPPlacerTourItemDetailHelper.processSubProject(this.subProject);
      this.projectContactGroups =
        ImmoIPPlacerTourItemDetailHelper.getProjectContactGroups(
          this.subProject,
          this.groupProjectContactsPipe,
          this.formatPhoneNumber
        );
      this.timeBudgetDisplay =
        ImmoIPPlacerTourItemDetailHelper.getTimeBudgetDisplay(this.subProject);
      this.appointmentInfo =
        ImmoIPPlacerTourItemDetailHelper.getAppointmentInfo(
          this.subProject,
          this.printAddressPipe,
          this.dateTimePipe,
          this.formatPhoneNumber
        );
      const productWrappers = ImmoIPPlacerTourItemDetailHelper.processProducts(
        this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT,
        this.subProject.project == null
          ? false
          : this.subProject.project.collaborative,
        this.subProject.project == null
          ? null
          : this.subProject.project.products,
        this.immoIPPlacerTourItemDetailChangeStorageHandler,
        this.subProject.projectPlacement.placementFloor
      );
      this.firstIPBoardWrapper = CollectionHelper.isPopulated(productWrappers)
        ? productWrappers[0]
        : null;
      if (this.subProject.type !== SUB_PROJECT_TYPES.BASE_PROJECT) {
        this.adjustment = <Adjustment>this.subProject;
        this.baseProjectPlacement = this.adjustment.baseProjectPlacement;
        this.baseProjectTour = this.adjustment.baseProjectTour;
        this.baseProjectDoneRegistration =
          this.adjustment.baseProjectDoneRegistration;
        if (this.subProject.type === SUB_PROJECT_TYPES.SOLDRENTED) {
          this.soldRentedStickerInfo =
            ImmoIPPlacerTourItemDetailHelper.getSoldRentedStickerInfo(
              <SoldRented>this.subProject
            );
        }
      }
      this.subProjectToDoItems =
        ImmoIPPlacerTourItemDetailHelper.getSubProjectToDoItems(
          this.subProject,
          this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
            ? this.subProject.projectPlacement
            : this.baseProjectPlacement,
          this.firstIPBoardWrapper.ipBoard,
          this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT,
          this.immoIPPlacerTourItemDetailChangeStorageHandler
        );
      this.executionState =
        ImmoIPPlacerTourItemDetailHelper.getSubProjectExecutionState(
          this.subProject,
          this.firstIPBoardWrapper,
          this.subProjectToDoItems
        );
    } finally {
      this.init = true;
    }
  }

  resetNotExecuted(): void {
    this.modalService.confirmDialog(
      'Reset niet uitgevoerd',
      'Bent u zeker dat u niet uitgevoerd wilt resetten?',
      (b: boolean) => {
        if (b) {
          this.doReset(false);
        }
      }
    );
  }

  private doReset(executed: boolean): void {
    ImmoIPPlacerTourItemDetailHelper.handleReset(
      this.subProject,
      this.immoIPPlacerTourItemDetailChangeStorageHandler,
      executed
    ).finally(() => {
      this.notExecutedHistoryItemObject = null;
      this.updateExecutionState();
      if (executed) {
        this.startPlacement();
      }
    });
  }

  startPlacement(): void {
    ImmoIPPlacerTourItemDetailHelper.handleStartPlacement(
      this.subProject,
      this.user,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
    );
    this.updateExecutionState();
  }

  submitExecuted(): void {
    const submitPromise: Promise<void> =
      ImmoIPPlacerTourItemDetailHelper.handleSubmitExecuted(
        this.subProject,
        this.user,
        this.immoIPPlacerTourItemDetailChangeStorageHandler
      );
    this.updateExecutionState();
    this.returnToList(submitPromise);
  }

  openNotExecutedModal(): void {
    this.modalService.openComponentInModal(
      ImmoIPPlacerTourItemDetailNotExecutedModalComponent,
      [
        {
          key: 'immoIPPlacerTourItemDetailChangeStorageHandler',
          value: this.immoIPPlacerTourItemDetailChangeStorageHandler,
        },
        { key: 'subProject', value: this.subProject },
        { key: 'disabled', value: this.executionState.executed != null },
        {
          key: 'shouldReloadNotExecutedHistoryItemAssets',
          value: this.shouldReloadNotExecutedHistoryItemAssets,
        },
        { key: 'user', value: this.user },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.afterClosed(),
        (historyItem: ImmoIPNotExecutedHistoryItemObject) => {
          if (historyItem != null) {
            this.notExecutedHistoryItemObject = historyItem;
            const submitPromise: Promise<void> =
              ImmoIPPlacerTourItemDetailHelper.handleSubmitNotExecuted(
                this.subProject,
                this.user,
                historyItem,
                this.immoIPPlacerTourItemDetailChangeStorageHandler
              );
            this.updateExecutionState();
            this.returnToList(submitPromise);
          }
        }
      ),
      { width: '50vw', maxHeight: '95vh' }
    );
  }

  private returnToList(submitPromise: Promise<void>): void {
    submitPromise
      .then(async () => {
        const tour = await this.offlineStorageTourService.getTour(
          DateHelper.formatDate(this.date, 'yyyyMMdd')
        );
        const allExecuted = tour.tourItems.every((item) =>
          ImmoIPPlacerTourViewHelper.isExecutedOrDelayed(item)
        );
        if (allExecuted) {
          this.modalService.openComponentInModal(
            CompletedAssignmentModalComponent,
            [
              {
                key: 'user',
                value: this.user,
              },
              {
                key: 'message',
                value: 'Jouw toer is volledig klaar.',
              },
            ],
            new ObservableHandlerPair(
              (modalRef) => modalRef.afterClosed(),
              () => {}
            ),
            { width: '450px', maxHeight: '300px' }
          );
        }
        this.doReturnToList();
      })
      .catch((err) => {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.doReturnToList();
        }
      });
  }

  private doReturnToList() {
    this.router.navigateByUrl('/ip-installateur');
  }

  installerFeedbackChanged(value: string, product: Product): void {
    product.productPlacement.installerFeedback = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineProductChange(
      product,
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeInstallerFeedback
    );
    this.updateExecutionState();
  }

  placerFeedbackChanged(value: string): void {
    this.subProject.projectPlacement.placerFeedback = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler.changePlacerFeedback
    );
    this.updateExecutionState();
  }

  internalInfoChanged(info: string, recurring: boolean): void {
    ImmoIPPlacerTourItemDetailHelper.handleSubmitInternalInfo(
      { info, recurring },
      this.immoIPPlacerTourItemDetailChangeStorageHandler
    );
  }

  toDoItemChanged(toDoItem: ToDoItem, product: Product, prop: string): void {
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeProductToDoItem(
      product,
      toDoItem,
      prop,
      this.immoIPPlacerTourItemDetailChangeStorageHandler.changeProductToDoItem
    );
    this.updateExecutionState();
  }

  subProjectToDoItemChanged(toDoItem: ToDoItem, prop: string): void {
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeSubProjectToDoItem(
      toDoItem,
      prop,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeSubProjectToDoItem
    );
    this.updateExecutionState();
  }

  productAssetStored(product: Product): void {
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineProductChange(
      product,
      product.productAssets,
      this.immoIPPlacerTourItemDetailChangeStorageHandler.productAssetAdded
    );
    this.updateExecutionState();
    ImmoIPPlacerTourItemDetailHelper.handleExecutionAssetAdded(
      this.subProject,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
    );
  }

  projectAssetStored(): void {
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      this.subProject.projectPlacement.projectPlacementAssets,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .projectPlacementAssetAdded
    );
    this.updateExecutionState();
    ImmoIPPlacerTourItemDetailHelper.handleExecutionAssetAdded(
      this.subProject,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
    );
  }

  productAssetDeleted(product: Product): void {
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineProductChange(
      product,
      product.productAssets,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .productAssetDeletedFunction
    );
    this.updateExecutionState();
  }

  projectAssetDeleted() {
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      this.subProject.projectPlacement.projectPlacementAssets,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .adjustmentAssetDeletedFunction
    );
    this.updateExecutionState();
  }

  openDetailAssetLightbox(
    designAssets: TourItemDetailAsset[],
    startIndex?: number
  ): void {
    const album: IAlbum[] = designAssets.map((a) => {
      return {
        src: `/api/assets/${a.uuid}`,
        thumb: `/api/assets/${a.uuid}`,
      };
    });
    this._lightbox.open(album, startIndex != null ? startIndex : 0);
  }

  private updateExecutionState(): void {
    this.executionState =
      ImmoIPPlacerTourItemDetailHelper.getSubProjectExecutionState(
        this.subProject,
        this.firstIPBoardWrapper,
        this.subProjectToDoItems
      );
  }

  contentChanged(value: string) {
    this.notExecutedHistoryItemObject.content = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeNotExecutedHistoyItemContent
    );
  }

  callClientChanged(value: boolean) {
    this.notExecutedHistoryItemObject.callClient = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeNotExecutedHistoyItemCallClient
    );
  }

  clientCommunicationSummaryChanged(value: string) {
    this.notExecutedHistoryItemObject.clientCommunicationSummary = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeNotExecutedHistoyItemClientCommunicationSummary
    );
  }

  callInpressChanged(value: boolean) {
    this.notExecutedHistoryItemObject.callInpress = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeNotExecutedHistoyItemCallInpress
    );
  }

  inpressCommunicationSummaryChanged(value: string) {
    this.notExecutedHistoryItemObject.inpressCommunicationSummary = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeNotExecutedHistoyItemInpressCommunicationSummary
    );
  }

  stockedAtBranchChanged(stockedAtBranch: boolean): void {
    this.stockedAtBranch = stockedAtBranch;
    if (!stockedAtBranch) {
      this.notExecutedHistoryItemObject.stockAtBranchId = null;
      this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
        null,
        this.immoIPPlacerTourItemDetailChangeStorageHandler
          .changeNotExecutedHistoyItemStockAtBranch
      );
    }
  }

  stockedAtBranchIdChanged(stockedAtBranchId: number): void {
    this.notExecutedHistoryItemObject.stockAtBranchId = null;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      stockedAtBranchId,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeNotExecutedHistoyItemStockAtBranch
    );
  }

  suggestedFollowUpChanged(value: string) {
    this.notExecutedHistoryItemObject.suggestedFollowUp = value;
    this.immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
        .changeNotExecutedHistoyItemSuggestedFollowUp
    );
  }

  async submitChanges() {
    await ImmoIPPlacerTourItemDetailHelper.handleSubmitChanges(
      this.notExecutedHistoryItemObject,
      this.immoIPPlacerTourItemDetailChangeStorageHandler
    );
    this.saved = true;
  }

  addressChanged(address: Address): void {
    ImmoIPPlacerTourItemDetailHelper.handleChangeAddress(
      this.subProject,
      address,
      this.immoIPPlacerTourItemDetailChangeStorageHandler,
      this.immoIPChangesService
    );
  }

  compareBoardTypesFn(t1: BoardType, t2: BoardType): boolean {
    return t1 && t2 ? t1.ordinal === t2.ordinal : t1 === t2;
  }

  comparePlacementTypesFn(t1: PlacementType, t2: PlacementType): boolean {
    return t1 && t2 ? t1.ordinal === t2.ordinal : t1 === t2;
  }

  async savePlacementConfigUpdate() {
    if (ProductHelper.isIPBoard(this.subProject.project.firstProduct)) {
      const ipBoard = <IPBoard>this.subProject.project.firstProduct;
      const boardPart = ipBoard.boardPart;
      if (
        this.selectedBoardType.ordinal !== boardPart.boardType.ordinal ||
        this.selectedPlacementType.ordinal !== boardPart.placementType.ordinal
      ) {
        if (
          (boardPart.boardType.ordinal !=
            BoardTypeChoices.SINGLE_SIDED.ordinal &&
            this.selectedBoardType.ordinal ==
              BoardTypeChoices.SINGLE_SIDED.ordinal) ||
          (this.selectedBoardType.ordinal ==
            BoardTypeChoices.DOUBLE_SIDED.ordinal &&
            ipBoard.boardPart.boardType.ordinal ===
              BoardTypeChoices.V.ordinal &&
            (ProductHelper.doTitleSidesDiffer(ipBoard.boardPart) ||
              ProductHelper.doPhoneNumberSidesDiffer(ipBoard.boardPart)))
        ) {
          this.modalService.confirmDialog(
            'Bord type wijzigen',
            'Bent u zeker dat u het bordtype wil wijzigen voor een bord met verschillende zijdes?',
            async (b) => {
              if (b) {
                await this.modifyBoard();
              }
            }
          );
        } else {
          await this.modifyBoard();
        }
      }
    }
    this.showPlacementConfigModal = !this.showPlacementConfigModal;
  }

  private async modifyBoard() {
    this.immoIPPlacerTourItemDetailChangeStorageHandler.resetToDoItems();
    this.subProjectToDoItems =
      ImmoIPPlacerTourItemDetailHelper.getSubProjectToDoItems(
        this.subProject,
        this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
          ? this.subProject.projectPlacement
          : this.baseProjectPlacement,
        this.firstIPBoardWrapper.ipBoard,
        this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT,
        this.immoIPPlacerTourItemDetailChangeStorageHandler
      );
    if (this.executionState != null) {
      this.executionState.allowExecuted = false;
    }

    const dateString =
      this.date != null ? moment(this.date).format('YYYYMMDD') : null;

    try {
      const modifiedSubProject =
        await ImmoIPPlacerTourItemDetailHelper.handleSubmitPlacementConfig(
          this.selectedBoardType,
          this.selectedPlacementType,
          this.immoIPPlacerTourItemDetailChangeStorageHandler
        );
      await this.doLoadSubProject(
        {
          value: modifiedSubProject,
        },
        dateString
      );
      this.snackService.showSuccess('Plaatsing config succesvol aangepast');
    } catch (err) {
      if (ErrorHelper.isConnectionLostError(err)) {
        this.immoIPPlacerTourItemDetailChangeStorageHandler.applyModifiedConfigToSubProject(
          this.subProject
        );
        ImmoIPPlacerTourItemDetailHelper.prepareForReloadDueToModifiedConfig(
          this.subProject
        );
        await this.doLoadSubProject(
          {
            value: this.subProject,
          },
          dateString
        );
      } else {
        throw err;
      }
    }
  }

  // Returns the installer PDF URL(s) for a given boardPart
  // Can return: null (no PDF), string (single PDF), or object (multiple PDFs)
  getInstallerPdfUrl(
    boardPart: any
  ): string | null | { [key: string]: string } {
    if (!boardPart || !boardPart.boardType || !boardPart.placementType) {
      return null;
    }
    const boardTypeOrdinal = boardPart.boardType.ordinal;
    const placementTypeOrdinal = boardPart.placementType.ordinal;
    const pdfEntry =
      this.installerPdfMatrix[boardTypeOrdinal]?.[placementTypeOrdinal];

    if (!pdfEntry) {
      return null;
    }

    // If it's an object (multiple options), return the object
    if (typeof pdfEntry === 'object' && pdfEntry !== null) {
      const urls: { [key: string]: string } = {};
      for (const [key, filename] of Object.entries(pdfEntry)) {
        if (filename) {
          urls[key] = `/assets/installer-pdfs/${filename}`;
        }
      }
      return Object.keys(urls).length > 0 ? urls : null;
    }

    // If it's a string (single option), return the URL
    if (typeof pdfEntry === 'string' && pdfEntry) {
      return `/assets/installer-pdfs/${pdfEntry}`;
    }

    return null;
  }

  // Helper method to get object keys for template iteration
  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  // Helper method to get user-friendly button labels for PDF options
  getPdfButtonLabel(optionKey: string): string {
    const labelMap: { [key: string]: string } = {
      regular: 'Bekijk installatieinstructies (horizontaal)',
      vertical: 'Bekijk installatieinstructies (verticaal)',
    };
    return (
      labelMap[optionKey] || `Bekijk installatieinstructies (${optionKey})`
    );
  }

  // Helper method for template type checking
  isObject(value: any): boolean {
    return typeof value === 'object' && value !== null;
  }

  // Helper method for template type checking
  isString(value: any): boolean {
    return typeof value === 'string';
  }

  // Check if current user is an installer (should have read-only access to recurring info checkbox)
  isInstaller(): boolean {
    return this.user != null && UserHelper.isIPPlacer(this.user);
  }
}
